import * as Sentry from '@sentry/react'
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  clearPhantomSolanaSession,
  consumePhantomSolanaCallback,
  getPhantomSolanaConnectUrl,
  getPhantomSolanaSignTransactionUrl,
  getStoredPhantomSolanaSession,
  hasPhantomSolanaCallbackParams,
  removePhantomSolanaCallbackParams,
  type PhantomSolanaSession,
} from '@/utils/phantomSolanaMobile'

type PhantomSolanaMobileContextValue = {
  callbackError: Error | null
  connect: (cluster: 'devnet' | 'mainnet-beta' | 'testnet') => void
  disconnect: () => void
  session: PhantomSolanaSession | null
  sessionCallbackSequence: number
  signedTransaction: PhantomSignedTransaction | null
  signTransaction: (input: PhantomSignedTransaction) => void
  takeSignedTransaction: (orderId: string) => Uint8Array | null
}

type PhantomSignedTransaction = {
  orderId: string
  serializedTransaction: Uint8Array
}

export const PhantomSolanaMobileContext =
  createContext<PhantomSolanaMobileContextValue>({
    callbackError: null,
    connect: () => undefined,
    disconnect: () => undefined,
    session: null,
    sessionCallbackSequence: 0,
    signedTransaction: null,
    signTransaction: () => undefined,
    takeSignedTransaction: () => null,
  })

export const PhantomSolanaMobileProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [session, setSession] = useState(getStoredPhantomSolanaSession)
  const [sessionCallbackSequence, setSessionCallbackSequence] = useState(0)
  const [signedTransaction, setSignedTransaction] =
    useState<PhantomSignedTransaction | null>(null)
  const signedTransactionRef = useRef<PhantomSignedTransaction | null>(null)
  const [callbackError, setCallbackError] = useState<Error | null>(null)

  useEffect(() => {
    if (!hasPhantomSolanaCallbackParams()) return

    try {
      const result = consumePhantomSolanaCallback()
      if (result?.type === 'connect') {
        setSession(result.session)
        setSessionCallbackSequence((sequence) => sequence + 1)
      } else if (result?.type === 'signTransaction') {
        const nextSignedTransaction = {
          orderId: result.orderId,
          serializedTransaction: result.serializedTransaction,
        }
        signedTransactionRef.current = nextSignedTransaction
        setSignedTransaction(nextSignedTransaction)
      }
    } catch (error) {
      const nextError =
        error instanceof Error
          ? error
          : new Error('Unable to process the Phantom response')
      setCallbackError(nextError)
      setSession(getStoredPhantomSolanaSession())
      Sentry.captureException(nextError, {
        tags: { action: 'phantom_solana_mobile_callback' },
      })
    } finally {
      removePhantomSolanaCallbackParams()
    }
  }, [])

  const connect = useCallback(
    (cluster: 'devnet' | 'mainnet-beta' | 'testnet') => {
      setCallbackError(null)
      window.location.assign(getPhantomSolanaConnectUrl({ cluster }))
    },
    []
  )

  const disconnect = useCallback(() => {
    clearPhantomSolanaSession()
    setSession(null)
  }, [])

  const signTransaction = useCallback(
    ({ orderId, serializedTransaction }: PhantomSignedTransaction) => {
      if (!session) throw new Error('Connect Phantom before signing')
      setCallbackError(null)
      window.location.assign(
        getPhantomSolanaSignTransactionUrl({
          orderId,
          serializedTransaction,
          session,
        })
      )
    },
    [session]
  )

  const takeSignedTransaction = useCallback((orderId: string) => {
    const value = signedTransactionRef.current
    if (value?.orderId !== orderId) return null

    signedTransactionRef.current = null
    setSignedTransaction(null)
    return value.serializedTransaction
  }, [])

  const value = useMemo(
    () => ({
      callbackError,
      connect,
      disconnect,
      session,
      sessionCallbackSequence,
      signedTransaction,
      signTransaction,
      takeSignedTransaction,
    }),
    [
      callbackError,
      connect,
      disconnect,
      session,
      sessionCallbackSequence,
      signedTransaction,
      signTransaction,
      takeSignedTransaction,
    ]
  )

  return (
    <PhantomSolanaMobileContext.Provider value={value}>
      {children}
    </PhantomSolanaMobileContext.Provider>
  )
}
